import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import { Promoter } from '@clareefai/types';

import KanbanBoard from '../../components/shared/KanbanBoard';
import { useGetPromotersQuery } from '../../store/promoterApi';
import type { PromoterResponse } from '../../store/promoterApi';
import {
  deriveProgressFromTestimonialsAndMeetings,
  getPromoterLevelKey,
} from '../../utils/promoterLevel';

const mapPromoterResponseToPromoter = (response: PromoterResponse): Promoter => {
  let status: Promoter['status'] = 'invited';
  const { reviewCompleted, testimonialCompleted, useCaseCompleted, firstMeetingCompleted } =
    deriveProgressFromTestimonialsAndMeetings(response);

  if (firstMeetingCompleted && useCaseCompleted && testimonialCompleted && reviewCompleted) {
    status = 'available';
  } else if (useCaseCompleted && testimonialCompleted && reviewCompleted) {
    status = 'testimonial_done';
  } else if (testimonialCompleted && reviewCompleted) {
    status = 'testimonial_ready';
  } else if (reviewCompleted) {
    status = 'review';
  } else if (response.status) {
    const statusMap: Record<string, Promoter['status']> = {
      invited: 'invited',
      review: 'review',
      testimonial_ready: 'testimonial_ready',
      testimonial_done: 'testimonial_done',
      available: 'available',
    };
    status = statusMap[response.status] || 'invited';
  }

  return {
    id: response.id || '',
    name: `${response.firstName} ${response.lastName}`,
    email: response.email,
    phone: response.phone || '',
    company: response.company || '',
    avatar: response.avatar,
    status,
    prospectsReferred: response.prospectsReferred || 0,
    conversionRate: response.conversionRate || 0, // Keep static as requested
    totalRewards: response.totalRewards || 0, // Keep static as requested
    joinedDate: response.joinedDate || response.createdAt || ''
  };
};

const PromotersKanbanIkigai: React.FC = () => {
  const { t } = useTranslation();
  const { data: promotersData, isLoading, error } = useGetPromotersQuery();
  const navigate = useNavigate();

  // Map backend data to frontend format
  const promoters = useMemo(() => {
    if (!promotersData) return [];
    return promotersData.map(mapPromoterResponseToPromoter);
  }, [promotersData]);

  const promotersByLevel = useMemo(() => {
    if (!promotersData) return {
      invited_promoter: [] as Promoter[],
      advocate_promoter: [] as Promoter[],
      champion_promoter: [] as Promoter[],
      ambassador: [] as Promoter[],
    };

    const grouped = {
      invited_promoter: [] as Promoter[],
      advocate_promoter: [] as Promoter[],
      champion_promoter: [] as Promoter[],
      ambassador: [] as Promoter[],
    };

    promotersData.forEach((response) => {
      const level = getPromoterLevelKey(response);
      const promoter = mapPromoterResponseToPromoter(response);
      // DEV-84 — invited-only promoters (no review yet) used to be filtered out.
      // Show them alongside Level 1 so vendors see newly-invited promoters.
      const bucket = level === 'invited_only' ? 'invited_promoter' : level
      grouped[bucket as keyof typeof grouped].push(promoter);
    });

    return grouped;
  }, [promotersData]);

  const columns = useMemo(() => [
    {
      id: 'invited_promoter',
      title: t('promoters.power.level1Title'),
      subtitle: t('promoters.power.level1Desc'),
      items: promotersByLevel.invited_promoter,
      color: 'bg-blue-100 text-blue-800',
      headerColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      borderColor: '#3b82f6'
    },
    {
      id: 'advocate_promoter',
      title: t('promoters.power.advocateTitle'),
      subtitle: t('promoters.power.advocateDesc'),
      items: promotersByLevel.advocate_promoter,
      color: 'bg-green-100 text-green-800',
      headerColor: 'bg-gradient-to-r from-green-500 to-emerald-500',
      borderColor: '#10b981'
    },
    {
      id: 'champion_promoter',
      title: t('promoters.power.championTitle'),
      subtitle: t('promoters.power.championDesc'),
      items: promotersByLevel.champion_promoter,
      color: 'bg-purple-100 text-purple-800',
      headerColor: 'bg-gradient-to-r from-purple-500 to-violet-500',
      borderColor: '#8b5cf6'
    },
    {
      id: 'ambassador',
      title: t('promoters.power.ambassadorTitle'),
      subtitle: t('promoters.power.ambassadorDesc'),
      items: promotersByLevel.ambassador,
      color: 'bg-orange-100 text-orange-800',
      headerColor: 'bg-gradient-to-r from-orange-500 to-red-500',
      borderColor: '#f97316'
    }
  ], [promotersByLevel, t]);

  const handlePromoterClick = (promoterId: string) => {
    navigate(`/promoters/${promoterId}`);
  };

  const getLevelLabel = (levelKey: string) => {
    const labels: Record<string, string> = {
      invited_promoter: t('promoters.ikigai.levelShort.level1'),
      advocate_promoter: t('promoters.ikigai.levelShort.advocate'),
      champion_promoter: t('promoters.ikigai.levelShort.champion'),
      ambassador: t('promoters.ikigai.levelShort.ambassador'),
    };
    return labels[levelKey] ?? t('common.unknown');
  };

  const renderCard = (promoter: Promoter) => {
    const originalResponse = promotersData?.find((p) => p.id === promoter.id);
    const ikigaiStatus = originalResponse ? getPromoterLevelKey(originalResponse) : 'invited_promoter';
    
    return (
      <div
        className="space-y-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
        onClick={() => handlePromoterClick(promoter.id)}
      >
        <div className="flex items-center space-x-3">
          <Avatar
            src={promoter.avatar}
            name={promoter.name}
            size="md"
            className="border-2 border-white shadow-sm"
          />
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate">{promoter.name}</h4>
            <p className="text-sm text-gray-600 flex items-center truncate">
              <Building className="w-3 h-3 mr-1 flex-shrink-0" />
              {promoter.company}
            </p>
          </div>
        </div>

        {/* Statistiques compactes */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="text-center">
            <div className="font-semibold text-blue-600">{promoter.prospectsReferred}</div>
            <div className="text-gray-500">{t('common.prospects')}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-green-600">{promoter.conversionRate}%</div>
            <div className="text-gray-500">{t('common.conversion')}</div>
          </div>
        </div>

        {/* Récompenses */}
        <div className="text-center pt-2 border-t border-gray-200">
          <div className="text-sm font-semibold text-yellow-600">
            {promoter.totalRewards.toLocaleString()}€
          </div>
          <div className="text-xs text-gray-500">{t('promoters.ikigai.totalRewards')}</div>
        </div>

        {/* Badge de statut Ikigai */}
        <div className="pt-2">
          <span className={`status-badge ${
            ikigaiStatus === 'invited_promoter' ? 'status-review' :
            ikigaiStatus === 'advocate_promoter' ? 'status-available' :
            ikigaiStatus === 'champion_promoter' ? 'status-completed' :
            ikigaiStatus === 'ambassador' ? 'status-available' :
            'status-invited'
          }`}>
            {getLevelLabel(ikigaiStatus)}
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">{t('promoters.kanbanBoard.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">{t('promoters.kanbanBoard.loadError')}</div>
      </div>
    );
  }

  return (
    <KanbanBoard
      columns={columns}
      renderCard={renderCard}
    />
  );
};

export default PromotersKanbanIkigai;